import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { registerServiceWorker } from './registerSW';
import { initObservability } from './utils/observability';
import { ToastProvider, injectThemeVars } from '@turn-connect/ui';
import { ThemeProvider } from './theme/ThemeProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queries/queryClient';
import { registerWorkOrderCacheSync } from './queries/workOrderCacheSync';
import { announceWorkOrderChanged } from './utils/workOrderEvents';
import { shouldAnnounceChange, locationIdFromUrl } from './utils/dashboardMutationSignal';

// Global fetch interceptor to auto-logout on auth failures
// URLs that should NOT trigger logout on 401 (optional/non-critical endpoints)
// These endpoints may legitimately return 401 during normal operation
const SKIP_LOGOUT_PATHS = [
  '/users/me/mentions',
  '/mentions/',
  '/terms/',
  '/accounts', // Admin accounts list - prevent logout on dashboard load
  '/locations', // Locations list - prevent logout on dashboard load
];

function shouldSkipLogoutFor401(url: string): boolean {
  try {
    const urlPath = new URL(url, window.location.origin).pathname;
    return SKIP_LOGOUT_PATHS.some((path) => urlPath.includes(path));
  } catch {
    return false;
  }
}

// Work-order mutations must invalidate the caches they move even when the
// view that shows them is unmounted — see workOrderCacheSync.
registerWorkOrderCacheSync(queryClient);

const originalFetch = window.fetch.bind(window);
window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const response = await originalFetch(input, init);
  try {
    if (!response) return response;
    // Any successful work-order mutation tells views showing derived counts to
    // refresh. Central on purpose: the explicit created/closed announcements
    // only covered what a call site remembered, which is why the location
    // dashboard went stale on every other action.
    try {
      const changed = shouldAnnounceChange(input, init, response.ok, window.location.origin);
      if (changed) announceWorkOrderChanged({ locationId: locationIdFromUrl(changed), path: changed.pathname });
    } catch {
      // A refresh hint must never break the request it is observing.
    }
    if (response.status === 401) {
      const requestUrl = typeof input === 'string' ? input : (input as Request)?.url || '';
      if (!shouldSkipLogoutFor401(requestUrl)) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      return response;
    }
    if (response.status === 400) {
      // Check body for JWT-related errors without consuming original stream
      const clone = response.clone();
      const ct = clone.headers.get('content-type') || '';
      let msg = '';
      try {
        if (ct.includes('application/json')) {
          const j = await clone.json();
          msg = (j && (j.error || j.message)) || '';
        } else {
          msg = await clone.text();
        }
      } catch {}
      const lower = (msg || '').toLowerCase();
      if (
        lower.includes('missing or malformed jwt') ||
        (lower.includes('jwt') && (lower.includes('missing') || lower.includes('malformed') || lower.includes('invalid')))
      ) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
    }
  } catch {}
  return response;
};

initObservability();

// Define the theme CSS variables before React renders, so the `data-theme` the
// boot script already set on <html> resolves to real colors on first paint.
injectThemeVars();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <App />
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>
);

// Register PWA service worker (no-op in dev)
registerServiceWorker();
